import { useTranslation } from "react-i18next";
import settingService from "api/settingsService";
import decreeService from "api/decreeService";
import prettyBytes from "pretty-bytes";
import { ISpaceUsageReponse } from "dto/Settings/ISpaceUsageResponse";
import { HubCallbacksNames } from "api/hub";
import AddIcon from "assets/icons/add.svg?react";
import SidebarFilter from "components/SidebarFilter/index";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import workspaceStyle from "./workspace.module.scss";
import { SignalRContext } from "../../App";

import {
  faAngleDown,
  faAngleUp,
  faEllipsisVertical,
  faGear,
  faHardDrive,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import casesService from "api/casesService";
import filterService from "api/filterService";
import LeftIcon from "assets/icons/arrow_left.svg?react";
import RightIcon from "assets/icons/arrow_right.svg?react";
import ButtonIcon from "assets/icons/button-profile.svg?react";
import FormIcon from "assets/icons/form.svg?react";
import CalendarIcon from "assets/icons/calenar_sidebar.svg?react";
import CaseIcon from "assets/icons/case_sidebar.svg?react";
import DriveIcon from "assets/icons/hard_drive.svg?react";
import HomeIcon from "assets/icons/home.svg?react";
import CourtPortalIcon from "assets/icons/court.svg?react";
import IncomingIcon from "assets/icons/new-incoming.svg?react";
import CouncilIcon from "assets/icons/office.svg?react";
import LocalLawIcon from "assets/icons/book.svg?react";
import OutgoingIcon from "assets/icons/new-outcoming.svg?react";
import NotaIcon from "assets/icons/nota.svg?react";
import ClientIcon from "assets/icons/person.svg?react";
import SettingsIcon from "assets/icons/settings_24px.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import AdminIcon from "assets/icons/admin.svg?react";
import HelpdeskIcon from "assets/icons/chati.svg?react";
import ChatIcon from "assets/icons/chat_bubble.svg?react";
import RegistryIcon from "assets/icons/view_list.svg?react";
import AcceptanceSignIcon from "assets/icons/signature.svg?react";
import BudgetIcon from "assets/icons/credit_card.svg?react";
import FkIcon from "assets/icons/book.svg?react";
import TaxIcon from "assets/icons/home_work.svg?react";
import WasteIcon from "assets/icons/bin.svg?react";
import ProcurementIcon from "assets/icons/court.svg?react";
import RecoveryCodeIcon from "assets/icons/qr2.svg?react";
import LogoSmallIcon from "assets/logo.svg?react";
import MenuIcon from "assets/icons/menu.svg?react";
import CloudIcon from "assets/icons/cloud.svg?react";
import SidebarAddingOptions from "components/SidebarAddingOptions";
import SidebarButton from "components/SidebarButton";
import ModalsManagerContext from "contexts/ModalsManager";
import ReactDOM from "react-dom";
import { getUserId } from "utils/getUserId";
import { clearAllKeys } from "utils/clearAllKeys";
import { useAuth } from "react-oidc-context";

import dossierService from "api/dossierService";
import Button from "components/Button";
import { ListType } from "dto/ListType";
import { Claims } from "dto/User/claims";
import { getUserClaim } from "utils/getUserClaim";
import { ICounter } from "dto/ICounter";
import { ECounterType } from "dto/ECounterType";
import { convertToLetters } from "utils/convertToLetters";
import {
  Avatar,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import usePrivilegesStore from "hooks/usePrivilegesStore";
import { AbilityContext, Can } from "contexts/AbilityContext";
import { EMenuFilters } from "dto/EMenuFilters";
import HollowButton from "components/HollowButton";
import Popper from "components/Popper";
import { EAppModule } from "dto/EAppModule";
import ViewIcon from "assets/icons/palette.svg?react";
import classNames from "classnames";
import { HubConnectionState } from "@microsoft/signalr";
import { useMediaQuery } from "usehooks-ts";
import { LanguageSwitcher } from "components/LanguageSwitcher/LanguageSwitcher";
import CompanyContext from "contexts/CompanyContext";
import { getCompanyId } from "utils/getCompanyId";
import DiskQuotaExceededModal from "components/DiskQuotaExceededModal";
import DiskSpaceLowModal from "components/DiskSpaceLowModal";
import { isDev } from "utils/isDev";

const AccountMenu = ({ toggle }: { toggle: () => void }) => {
  const auth = useAuth();
  const { company } = useContext(CompanyContext);
  const { modals, setModals } = useContext(ModalsManagerContext);

  const ref = useRef<HTMLDivElement>(null);

  // Zamknięcie karty po kliknięciu poza jej obszar.
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      toggle();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [toggle]);

  const logout = () => {
    auth.signoutRedirect();
    sessionStorage.removeItem("workspaceId");
    localStorage.removeItem("workspaceId");
    clearAllKeys(getUserId(auth.user));
  };

  return ReactDOM.createPortal(
    <div ref={ref} className={styles.dropdown}>
      <div className={workspaceStyle.user}>
        <Avatar className={styles.avatar}>
          {convertToLetters(auth.user?.profile?.name ?? "")}
        </Avatar>
        <div className={workspaceStyle.cname}>
          <div className={workspaceStyle.ctitle}>
            {auth.user?.profile?.name}{" "}
          </div>
          <div className={workspaceStyle.company}>{company?.Name}</div>
        </div>
      </div>

      <div className={workspaceStyle.buttonsWrapper}>
        <SidebarButton
          title="Ustawienia"
          Icon={SettingsIcon}
          handleClick={() => {
            toggle();
            setModals({ ...modals, settings: true, hide: false });
          }}
          showCounter={false}
          isActive={false}
        ></SidebarButton>

        <div className={styles.btn}>
          <Button
            className={styles.btn}
            text="Wyloguj"
            onClick={() => {
              logout();
              toggle();
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

const Sidebar = ({
  setIsScroll,
  handleExpand,
  isExpanded,
}: {
  setIsScroll?: (state: boolean) => void;
  isExpanded?: boolean;
  handleExpand?: () => void;
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  // Model kluczy firmowych: profile znikły, tożsamość "z kim pracuje zalogowany" niesie
  // teraz CompanyContext (components/CompanyProvider woła /api/company/current raz, na
  // szczycie drzewa). Konto bez firmy (D2) nie dostaje już awaryjnie utworzonego profilu
  // "Prywatny" — bramkę pokazuje wyżej w drzewie NoCompanyGate.
  const { company } = useContext(CompanyContext);

  const isGuest = auth?.user?.profile?.role === "guest";
  const isMobile = useMediaQuery("(max-width: 767px)");

  const uiPrivileges = usePrivilegesStore((state) => state.privileges);
  const uiVariant = usePrivilegesStore((state) => state.active);
  const switchToFreeUI = usePrivilegesStore((state) => state.switchToFree);
  const switchToStandardUI = usePrivilegesStore(
    (state) => state.swtichToStandard,
  );
  const switchToPremiumUI = usePrivilegesStore(
    (state) => state.swtichToPremium,
  );
  const toggleModuleUI = usePrivilegesStore((state) => state.toggleModule);
  const ability = useContext(AbilityContext);
  const [forceMenuUpdate, setForceMenuUpdate] = useState(0);

  usePrivilegesStore.subscribe(() => {
    console.log("Can access drive", ability.can("accessModule", "drive"));
    //setMenus(isGuest ? GuestMenu : ActiveMenu);
    setForceMenuUpdate(forceMenuUpdate + 1);
  });

  const handleDebugChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Switching UI", event.target.value);
    switch (event.target.value) {
      case "free":
        switchToFreeUI();
        break;
      case "standard":
        switchToStandardUI();
        break;
      case "premium":
        switchToPremiumUI();
        break;
    }
  };

  const GuestMenu: any = [
    {
      id: 1,
      fullId: "1B8AE8D3-3209-497D-9526-27608FFFA84D",
      icon: IncomingIcon,
      title: t("sidebar.incoming"),
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 10,
      visible: () => true,
      isBeta: false,
    },
    {
      id: 2,
      fullId: "sending",
      icon: OutgoingIcon,
      title: t("sidebar.outgoing"),
      isOpen: false,
      filters: [],
      filtersHardCoded: false,
      canAddNewFilter: true,
      showCounter: false,
      hideExpander: false,
      counterId: 11,
      visible: () => true,
      isBeta: false,
    },
    {
      id: 3,
      icon: CaseIcon,
      title: t("sidebar.cases"),
      name: "cases",
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 13,
      visible: () => true,
      isBeta: false,
    },
  ];

  const ActiveMenu: any = [
    {
      id: "a8e223ab-de08-4526-8ce3-26a29d1f2356",
      fullId: "a8e223ab-de08-4526-8ce3-26a29d1f2356",
      icon: CourtPortalIcon,
      title: t("sidebar.courtPortal"),
      isOpen: false,
      filters: [
        {
          Id: "058f927c-aeb4-4a30-b8ba-8e6313031567",
          uniqName: "piIncoming",
          Name: t("sidebar.deliveryRegistry"),
          counter: 0,
        },
        {
          Id: "058f927c-aeb4-4a30-b8ba-8e6313031568",
          uniqName: "piIncoming2",
          Name: t("sidebar.deliveryInbox"),
          counter: 0,
        },
        {
          Id: "cde5e2df-fd50-46d7-851a-dcce39ae71fb",
          uniqName: "piCalendar",
          Name: t("sidebar.courtDates"),
          counter: 0,
          isIncoming: false,
        },
        {
          Id: "90a820b2-8106-4632-8de0-264aba2e606c",
          uniqName: "piCaseRegistry",
          Name: t("sidebar.caseRegistry"),
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: true,
      visible: () => ability && ability.can("accessModule", "courtportal"),
      isBeta: false,
    },

    {
      id: "d76ad393-712b-4984-968a-f104579d6fa9",
      fullId: "d76ad393-712b-4984-968a-f104579d6fa9",
      icon: HomeIcon,
      title: t("sidebar.desk"),
      isOpen: false,
      isDesk: true,
      filters: [
        {
          Id: "c555b900-860a-4628-80ab-084d01076275",
          uniqName: "incoming",
          Name: t("sidebar.incoming"),
          counter: 0,
        },
        {
          Id: "9718808f-dcb8-4a07-a04c-f9de36b266ca",
          uniqName: "outgoing",
          Name: t("sidebar.outgoing"),
          counter: 0,
          isIncoming: false,
        },
        {
          Id: "d902a02c-16fa-4f88-8c0b-8f6c2f36ec71",
          uniqName: "cases",
          Name: t("sidebar.cases"),
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: true,
      visible: () => ability && ability.can("accessModule", "desk"),
      isBeta: false,
    },
    {
      id: "acceptance-sign-root",
      fullId: "acceptance-sign-root",
      icon: AcceptanceSignIcon,
      title: "Akceptacja i Podpis",
      isOpen: false,
      filters: [
        {
          Id: "as-received",
          uniqName: "as-received",
          Name: "Otrzymane",
          counter: 0,
          Static: true,
        },
        {
          Id: "as-cc",
          uniqName: "as-cc",
          Name: "Do wiadomości",
          counter: 0,
          Static: true,
        },
        {
          Id: "as-sent",
          uniqName: "as-sent",
          Name: "Wysłane",
          counter: 0,
          Static: true,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: true,
      visible: () => true,
      isBeta: false,
    },
    {
      id: "decrees-root",
      fullId: "decrees-root",
      icon: AcceptanceSignIcon,
      title: "Dekrety",
      isOpen: false,
      filters: [
        {
          Id: "decrees-received",
          uniqName: "decrees-received",
          Name: "Do wykonania",
          counter: 0,
          Static: true,
        },
        {
          Id: "decrees-issued",
          uniqName: "decrees-issued",
          Name: "Wydane",
          counter: 0,
          Static: true,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: true,
      visible: () => true,
      isBeta: false,
    },
    {
      id: "d1a2b3c4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
      fullId: "d1a2b3c4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
      icon: IncomingIcon,
      title: "Biuro podawcze",
      isOpen: false,
      filters: [
        {
          Id: "d1a2b3c4-e5f6-4a7b-8c9d-0e1f2a3b4c5e",
          uniqName: "biuro.incoming",
          Name: "Przychodzące",
          counter: 0,
        },
        {
          Id: "d1a2b3c4-e5f6-4a7b-8c9d-0e1f2a3b4c5f",
          uniqName: "biuro.outgoing",
          Name: "Wychodzące",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () =>
        true /*ability && ability.can("accessModule", "mailroom"),*/,
      isBeta: false,
    },
    {
      // Biuro rady — port modułu CouncilSessionAgenda z Magistratu.
      id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c60",
      fullId: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c60",
      icon: CouncilIcon,
      title: "Biuro rady",
      isOpen: false,
      filters: [
        {
          Id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c61",
          uniqName: "council.session",
          Name: "Sesja",
          counter: 0,
        },
        {
          Id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c62",
          uniqName: "council.archive",
          Name: "Archiwum sesji",
          counter: 0,
        },
        {
          Id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c63",
          uniqName: "council.conduct",
          Name: "Prowadzenie obrad",
          counter: 0,
        },
        {
          Id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c64",
          uniqName: "council.councilors",
          Name: "Radni",
          counter: 0,
        },
        {
          Id: "c3a1b2d4-5e6f-4a7b-8c9d-0e1f2a3b4c65",
          uniqName: "council.portal",
          Name: "Portal radnego",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () =>
        true /*ability && ability.can("accessModule", "council"),*/,
      isBeta: false,
    },
    {
      // Prawo miejscowe — port modułu Kwantum.SL.LocalLaw z Magistratu.
      // „Projekty" widzi każdy pracownik; pozostałe listy mają sens dla biura rady,
      // ale nie chowamy ich przed innymi — backend i tak odsiewa czynności po
      // uprawnieniach LocalLawAccess.
      id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d70",
      fullId: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d70",
      icon: LocalLawIcon,
      title: "Prawo miejscowe",
      isOpen: false,
      filters: [
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d71",
          uniqName: "locallaw.projects",
          Name: "Projekty",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d72",
          uniqName: "locallaw.approval",
          Name: "Do akceptacji",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d74",
          uniqName: "locallaw.publication",
          Name: "Do publikacji",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d73",
          uniqName: "locallaw.adopted",
          Name: "Uchwalone",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d77",
          uniqName: "locallaw.requests",
          Name: "Wnioski o publikację",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d75",
          uniqName: "locallaw.inforce",
          Name: "Akty obowiązujące",
          counter: 0,
        },
        {
          Id: "b7d3e1c2-4a5f-4b6c-8d9e-0f1a2b3c4d76",
          uniqName: "locallaw.permissions",
          Name: "Uprawnienia",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },
    {
      id: 1,
      fullId: EMenuFilters.UnhandledIncomignId,
      icon: IncomingIcon,
      title: t("sidebar.incoming"),
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 10,
      visible: () => ability && ability.can("accessModule", "incoming"),
      staticId: EMenuFilters.UnhandledIncomignId,
      isBeta: false,
    },
    {
      id: 2,
      fullId: "sending",
      icon: OutgoingIcon,
      title: t("sidebar.outgoing"),
      isOpen: false,
      filters: [],
      filtersHardCoded: false,
      canAddNewFilter: true,
      showCounter: false,
      hideExpander: false,
      counterId: 11,
      visible: () => ability && ability.can("accessModule", "outgoing"),
      isBeta: false,
    },

    {
      id: 3,
      icon: CaseIcon,
      title: t("sidebar.cases"),
      name: "cases",
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 13,
      visible: () => ability && ability.can("accessModule", "cases"),
      isBeta: false,
    },
    {
      id: 7,
      icon: DriveIcon,
      title: t("sidebar.drive"),
      name: "drive",
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 15,
      visible: () => ability && ability.can("accessModule", "drive"),
      isBeta: true,
    },

    {
      id: 6,
      fullId: "clients",
      icon: ClientIcon,
      title: t("sidebar.entities"),
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      visible: () => ability && ability.can("accessModule", "addressBook"),
      isBeta: true,
    },
    {
      id: 4,
      icon: CalendarIcon,
      title: t("sidebar.actions"),
      name: "actions",
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      counterId: 12,
      visible: () => ability && ability.can("accessModule", "tasks"),
      isBeta: true,
    },

    {
      id: "1b00b326-a7dc-43cb-822a-c725674fc424",
      fullId: "1b00b326-a7dc-43cb-822a-c725674fc424",
      icon: RegistryIcon,
      title: t("sidebar.registries"),
      isOpen: false,
      filters: [
        {
          Id: "fe598688-7d7b-43a7-b215-4b26f6ab21f4",
          uniqName: "registry.edelivery",
          Name: t("sidebar.edelivery"),
          counter: 0,
        },

        {
          Id: "b8f3c1a6-5d2e-4f7a-9c3b-1e2d3f4a5b6c",
          uniqName: "registry.piDeliverances",
          Name: "Doręczenia PI",
          counter: 0,
        },

        {
          Id: "4d41e4ad-0a54-41b9-9631-eb624dd41ad0",
          uniqName: "registry.notaparcels",
          Name: t("sidebar.encryptedParcels"),
          counter: 0,
          isStatic: true,
        },
        {
          Id: "37a4fda8-fc8d-4dbf-8d7c-a9509bc495f1",
          uniqName: "registry.cases",
          Name: t("sidebar.cases"),
          counter: 0,
          hidden: () => ability && !ability.can("accessModule", "cases"),
        },
        {
          Id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
          uniqName: "registry.contracts",
          Name: "Umowy",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "f6b8c0d2-1a3b-4c5d-9e7f-2b4c6d8e0a13",
      fullId: "f6b8c0d2-1a3b-4c5d-9e7f-2b4c6d8e0a13",
      icon: FkIcon,
      title: "FK",
      isOpen: false,
      filters: [
        {
          Id: "37a4fda8-fc8d-4dbf-8d7c-a9509bc495f2",
          uniqName: "registry.invoices",
          Name: "Dokumenty księgowe",
          counter: 0,
        },
        {
          Id: "f1e2d3c4-b5a6-4978-8c0d-1a2b3c4d5e6f",
          uniqName: "registry.zois",
          Name: "Zestawienie obrotów i sald",
          counter: 0,
        },
        {
          Id: "d4e5f6a7-b8c9-4d0e-8f1a-2b3c4d5e6f70",
          uniqName: "registry.rozrachunki",
          Name: "Rozrachunki",
          counter: 0,
        },
        {
          Id: "e7f8a9b0-c1d2-4e3f-9a8b-7c6d5e4f3a2b",
          uniqName: "registry.dziennik",
          Name: "Dziennik księgowań",
          counter: 0,
        },
        {
          Id: "d8e9f0a1-b2c3-4d5e-8f9a-0b1c2d3e4f5a",
          uniqName: "registry.rejestrVat",
          Name: "Rejestr VAT",
          counter: 0,
        },
        {
          Id: "a1f2e3d4-c5b6-4a7b-9c8d-0e1f2a3b4c5d",
          uniqName: "registry.jpk",
          Name: "Rejestr JPK",
          counter: 0,
        },
        {
          Id: "b3c4d5e6-f7a8-4b9c-8d0e-1f2a3b4c5d6e",
          uniqName: "registry.bilanse",
          Name: "Bilanse",
          counter: 0,
        },
        {
          Id: "c3d4e5f6-a7b8-9012-cdef-234567890abc",
          uniqName: "registry.kartotekaKontrahentow",
          Name: "Kartoteka kontrahentów",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "e4a7b0d5-4f5a-6b8c-bd2e-3d4e5f6a7b82",
      fullId: "e4a7b0d5-4f5a-6b8c-bd2e-3d4e5f6a7b82",
      icon: BudgetIcon,
      title: "Budżet",
      isOpen: false,
      filters: [
        {
          Id: "d3f6a9c4-3e4f-5a7b-ac1d-2c3d4e5f6a71",
          uniqName: "budget.report",
          Name: "Raport wydatków",
          counter: 0,
        },
        {
          Id: "b1d4e7a2-1c2d-4e5f-8a9b-0a1b2c3d4e5f",
          uniqName: "budget.plan",
          Name: "Lista wydatków",
          counter: 0,
        },
        {
          Id: "c2e5f8b3-2d3e-4f6a-9b0c-1b2c3d4e5f60",
          uniqName: "budget.engagements",
          Name: "Zaangażowania",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "a3d8f1c0-2b4e-4f6a-8c1d-3e5f7a9b0c11",
      fullId: "a3d8f1c0-2b4e-4f6a-8c1d-3e5f7a9b0c11",
      icon: TaxIcon,
      title: "Podatki i opłaty",
      isOpen: false,
      filters: [
        {
          Id: "a3d8f1c0-2b4e-4f6a-8c1d-3e5f7a9b0c12",
          uniqName: "tax.map",
          Name: "Mapa",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "b4e9f2d1-3c5f-4a7b-9d2e-4f6a8b0c1d22",
      fullId: "b4e9f2d1-3c5f-4a7b-9d2e-4f6a8b0c1d22",
      icon: WasteIcon,
      title: "Gospodarka odpadami",
      isOpen: false,
      filters: [
        {
          Id: "b4e9f2d1-3c5f-4a7b-9d2e-4f6a8b0c1d23",
          uniqName: "waste.map",
          Name: "Mapa",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "c5f0a3e2-4d6a-4b8c-ae3f-5a7b9c0d1e33",
      fullId: "c5f0a3e2-4d6a-4b8c-ae3f-5a7b9c0d1e33",
      icon: ProcurementIcon,
      title: "Zamówienia publiczne",
      isOpen: false,
      filters: [
        {
          Id: "c5f0a3e2-4d6a-4b8c-ae3f-5a7b9c0d1e34",
          uniqName: "procurement.open",
          Name: "Otwieranie zamówień",
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },

    {
      id: "3f5962b5-f97a-498c-93a6-c32a814d3283",
      fullId: "3f5962b5-f97a-498c-93a6-c32a814d3283",
      icon: ChatIcon,
      title: t("sidebar.chatModule"),
      filters: [],
      isOpen: false,
      filtersHardCoded: false,
      canAddNewFilter: false,
      showCounter: false,
      hideExpander: true,
      counterId: 28,
      visible: () => ability && ability.can("accessModule", "workspace"),
      isBeta: true,
      gap: true,
    },
    {
      id: 8,
      icon: ShareIcon,
      title: t("sidebar.shares"),
      name: "shares",
      isOpen: false,
      filters: [],
      canAddNewFilter: true,
      showCounter: false,
      visible: () => ability && ability.can("accessModule", "drive"),
      isBeta: true,
    },
    {
      id: "efadc816-ea34-4ba2-bd65-115ffef4ddf1",
      fullId: "efadc816-ea34-4ba2-bd65-115ffef4ddf1",
      icon: HelpdeskIcon,
      title: "Helpdesk",
      filters: [],
      isOpen: false,
      filtersHardCoded: false,
      canAddNewFilter: false,
      showCounter: false,
      hideExpander: true,
      // Korzeń Helpdesk ukryty w menu głównym.
      visible: () => false,
      isBeta: false,
      gap: true,
    },
    {
      id: "8f5c2bf1-dd9d-42b4-82a2-e8f7487c14a6",
      fullId: "8f5c2bf1-dd9d-42b4-82a2-e8f7487c14a6",
      icon: AdminIcon,
      title: "Admin",
      isOpen: false,
      isAdmin: true,
      counterId: 999,
      filters: [
        {
          Id: "3aa7c01a-69f9-4c42-90b4-ccc771d345ea",
          uniqName: "admin.users",
          Name: t("sidebar.clients"),
          counter: 0,
        },
        {
          Id: "782b2add-8e74-4b75-a713-e8e4c0cdaba1",
          uniqName: "admin.userRegistry",
          Name: t("sidebar.clientRegistry"),
          counter: 0,
          isStatic: true,
        },
        {
          Id: "dff08b80-1f02-4d26-a873-898aa7e36c25",
          uniqName: "admin.userWorkspaces",
          Name: t("sidebar.workspaceRegistry"),
          counter: 0,
        },
        {
          Id: "8876b45e-8c70-4454-866f-34a42f5ebb69",
          uniqName: "admin.baeRequests",
          Name: t("sidebar.baeRequestsRegistry"),
          counter: 0,
        },
        {
          Id: "b1e7c0a2-2f4d-4d9a-9c5e-7a1b6f0c3d11",
          uniqName: "admin.addressBookVerifications",
          Name: t("sidebar.addressBookVerifications"),
          counter: 0,
        },
      ],
      filtersHardCoded: true,
      canAddNewFilter: false,
      showCounter: true,
      // Korzeń Admin ukryty w menu głównym.
      visible: () => false,
    },
    {
      id: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a6",
      fullId: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a6",
      icon: FormIcon,
      title: t("sidebar.contactform"),
      name: "contactform",
      isOpen: false,
      filters: [],
      canAddNewFilter: false,
      hideExpander: true,
      showCounter: false,
      // Korzeń Formularz ukryty w menu głównym.
      visible: () => false,
      isBeta: false,
    },
    {
      id: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a8",
      fullId: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a8",
      icon: RecoveryCodeIcon,
      title: t("sidebar.recoveryCode"),
      name: "recoveryCode",
      isOpen: false,
      filters: [],
      canAddNewFilter: false,
      hideExpander: true,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },
    {
      id: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a7",
      fullId: "115c2bf1-dd9d-42b4-82a2-e8f7487c14a7",
      icon: SettingsIcon,
      title: t("sidebar.settings"),
      name: "settings",
      isOpen: false,
      filters: [],
      canAddNewFilter: false,
      hideExpander: true,
      showCounter: false,
      visible: () => true,
      isBeta: false,
    },
    {
      // Pozycja deweloperska — widoczna tylko na DEV (localhost / staging).
      id: "debug",
      fullId: "debug",
      icon: ViewIcon,
      title: "DEBUG",
      name: "debug",
      isOpen: false,
      filters: [],
      canAddNewFilter: false,
      hideExpander: true,
      showCounter: false,
      visible: () => true,
      isBeta: false,
      gap: true,
    },
  ];

  //const { user } = useContext(UserContext);

  const { mutate: fetchFilters } = filterService.useGetFiltersForCategory();

  const { mutate: fetchCasesFilters } = casesService.useGetFile();
  const { mutate: fetchDossierFilters } = dossierService.useGetFilters();

  // const { mutate: getDeskCounter } = deskService.useGetCount();
  const { modals, setModals } = useContext(ModalsManagerContext);

  const [menus, setMenus] = useState(isGuest ? GuestMenu : ActiveMenu);
  const [counters, setCounters] = useState<ICounter[]>([]);
  const [showUIDebugModal, setShowUIDebugModal] = useState<boolean>(false);
  const degubToolsRef = useRef();
  SignalRContext.useSignalREffect("counters", (data) => {
    setCounters(data);
  });

  // Licznik otwartych dekretów „do wykonania" — z REST (nie z liczników SignalR),
  // doklejany do listy liczników pod identyfikatorem filtra korzenia „Dekrety".
  const { data: decreeBoxData } = decreeService.useGetBox("received", false);
  const openDecrees = decreeBoxData?.data?.OpenReceivedCount ?? 0;
  const countersWithDecrees = useMemo<ICounter[]>(
    () =>
      openDecrees > 0
        ? [
            ...counters,
            { type: -1, filterId: "decrees-received", count: openDecrees },
          ]
        : counters,
    [counters, openDecrees],
  );

  // suma liczników ze wszystkich profili użytkownika, aktualizowana na żywo przez WS "counters"
  const totalCount =
    counters.find((c) => c.type === ECounterType.Total)?.count ?? 0;

  const {
    data: spaceUsageResponse,
    refetch: refetchSpaceUsage,
    isFetching: isFetchingSpaceUsage,
  } = settingService.useGetSpaceUsage();
  const [spaceUsage, setSpaceUsage] = useState<ISpaceUsageReponse | null>(null);

  useEffect(() => {
    if (spaceUsageResponse?.data) {
      setSpaceUsage(spaceUsageResponse.data);
    }
  }, [spaceUsageResponse]);

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc odświeżanie zajętości po takiej zmianie
  // jest zbędne (dawny reset przez prevProfileIdRef nigdy by się tu nie odpalił).
  const quotaExceededShownRef = useRef(false);
  const lowSpaceShownRef = useRef(false);

  // Aktualizacja zajętości dysku na żywo (WS) — tylko dla bieżącej firmy.
  // Uwaga: SignalR serializuje camelCase (inaczej niż REST/PascalCase), więc mapujemy
  // ręcznie na kształt ISpaceUsageReponse.
  SignalRContext.useSignalREffect(
    HubCallbacksNames.SpaceUsageChanged,
    (raw: {
      profileId?: string;
      used?: number;
      total?: number;
      filesSize?: number;
      dbSize?: number;
      variant?: number;
    }) => {
      const profileId = raw?.profileId;
      if (profileId && profileId !== getCompanyId()) {
        return;
      }

      setSpaceUsage({
        ProfileId: profileId,
        Used: raw?.used ?? 0,
        Total: raw?.total ?? 0,
        FilesSize: raw?.filesSize,
        DbSize: raw?.dbSize,
        Variant: raw?.variant,
      });
    },
    [],
  );

  const isSpaceUsageReady = !!(spaceUsage && spaceUsage.Total > 0);
  const spaceUsagePercent = isSpaceUsageReady
    ? (spaceUsage!.Used / spaceUsage!.Total) * 100
    : 0;

  // Po wykryciu przekroczenia limitu miejsca (>=100%) pokazujemy okno modalne.
  // Pokazujemy je raz na profil (ref zerowany przy zmianie profilu), aby nie
  // wyskakiwało przy każdej aktualizacji zajętości na żywo (WS).
  const [showQuotaExceeded, setShowQuotaExceeded] = useState(false);
  useEffect(() => {
    if (
      isSpaceUsageReady &&
      spaceUsagePercent >= 100 &&
      !quotaExceededShownRef.current
    ) {
      quotaExceededShownRef.current = true;
      setShowQuotaExceeded(true);
    }
  }, [isSpaceUsageReady, spaceUsagePercent]);

  // Ostrzeżenie o kończącym się miejscu (90–99%), również raz na profil.
  const [showLowSpace, setShowLowSpace] = useState(false);
  useEffect(() => {
    if (
      isSpaceUsageReady &&
      spaceUsagePercent >= 90 &&
      spaceUsagePercent < 100 &&
      !lowSpaceShownRef.current
    ) {
      lowSpaceShownRef.current = true;
      setShowLowSpace(true);
    }
  }, [isSpaceUsageReady, spaceUsagePercent]);

  useEffect(() => {
    console.log("reloading menu");
    setMenus(isGuest ? GuestMenu : ActiveMenu);
    // company w zależnościach — przebudowuje menu (i domknięcia visible()) po tym, jak
    // CompanyProvider dociągnie firmę asynchronicznie, dzięki czemu widoczność elementów
    // zależnych od firmy (np. Formularz) jest aktualna.
  }, [uiVariant, uiPrivileges, auth.user, auth.user?.profile, company]);

  const getFilters = (id: ListType) => {
    const menu = menus.find((menu: any) => menu.id === id);

    if (!menu) {
      return;
    }
    if (menu.showCounter) {
      // if (id === "d76ad393-712b-4984-968a-f104579d6fa9") {
      //   return;
      // }
      // if (id === "A666CC3E-A2AF-49AA-AB95-54DA6CB4CC99") {
      //   getDraftCounter(
      //     {},
      //     {
      //       onSuccess: (data) => {
      //         const newMenu = [...menus].map((menu) => {
      //           if (menu.id === id) {
      //             let incoming = menu.filters.find(
      //               (filter: any) => filter.uniqName === "edelivery"
      //             );
      //             let outgoing = menu.filters.find(
      //               (filter: any) => filter.uniqName === "email"
      //             );
      //             let cases = menu.filters.find(
      //               (filter: any) => filter.uniqName === "editorDocument"
      //             );
      //             if (!incoming || !outgoing || !cases) {
      //               return menu;
      //             }
      //             incoming.counter = data.data.edelivery;
      //             outgoing.counter = data.data.email;
      //             cases.counter = data.data.editorDocument;
      //           }
      //           return menu;
      //         });
      //         setMenus(newMenu);
      //       },
      //     }
      //   );
      // }
    }

    if (menu.filtersHardCoded) {
      return;
    }

    if (menu.hideExpander) {
      return;
    }

    if (id === 3) {
      fetchCasesFilters(
        {},
        {
          onSuccess: (data) => {
            const newMenus = [...menus].map((menu) => {
              if (menu.id === id) {
                menu.filters = data.data;
              }
              return menu;
            });

            setMenus(newMenus);
          },
        },
      );

      return;
    }

    if (id === 7) {
      fetchDossierFilters(
        { ListType: ListType.Drive },
        {
          onSuccess: (data) => {
            const newMenus = [...menus].map((menu) => {
              if (menu.id === id) {
                menu.filters = data.data;
              }
              return menu;
            });

            setMenus(newMenus);
          },
        },
      );

      return;
    }

    if (id === 8) {
      fetchDossierFilters(
        { ListType: ListType.Shares },
        {
          onSuccess: (data) => {
            const newMenus = [...menus].map((menu) => {
              if (menu.id === id) {
                menu.filters = data.data;
              }
              return menu;
            });

            setMenus(newMenus);
          },
        },
      );

      return;
    }

    fetchFilters(
      {
        id: id,
      },
      {
        onSuccess: (data) => {
          const newMenus = [...menus].map((menu) => {
            if (menu.id === id) {
              menu.filters = data.data;
            }
            return menu;
          });

          setMenus(newMenus);
        },
      },
    );
  };
  const testRef = useRef(null);
  const testRef2 = useRef(null);

  const checkSize = () => {
    if (!testRef?.current) return;
    if (!testRef2?.current) return;

    //@ts-ignore
    const maxScroll = testRef?.current.scrollHeight;
    //@ts-ignore
    const actualScroll = testRef2?.current.scrollHeight + 100 + 60;

    if (actualScroll >= maxScroll) {
      setIsScroll?.(true);
      return;
    }
    setIsScroll?.(false);
  };

  const handleChangeFilterState = (title: string) => {
    const newMenu = [...menus].map((menu) => {
      if (title === menu.title) {
        menu.isOpen = !menu.isOpen;
        if (!!menu.filters && menu.isOpen) {
          getFilters(menu.id);
        }
      }
      return menu;
    });

    setMenus(newMenu);
    setTimeout(() => {
      checkSize();
    }, 100);
  };

  const addButtonRef = useRef(null);

  const [, setIsHoverSettings] = useState<boolean>(false);

  const [options, setOptions] = useState(false);

  const [showList, setShowList] = useState(false);

  return (
    <div
      ref={testRef}
      className={`${styles.sidebar} ${!isExpanded && styles.isNotExpanded}`}
    >
      <div className={styles.twocolumns}>
        <div className={styles.col1}>
          <div className={styles.details}>
            {isExpanded && (
              <div
                onMouseEnter={() => setIsHoverSettings(true)}
                onMouseLeave={() => setIsHoverSettings(false)}
                className={styles.name}
              >
                <div className={styles.leftName}>
                  <span className={styles.nameWrapper}>
                    {auth.user?.profile.name}
                  </span>
                  <FontAwesomeIcon
                    onClick={() => setShowList(!showList)}
                    style={{ marginLeft: "4px" }}
                    className={styles.expander}
                    icon={showList ? faAngleUp : faAngleDown}
                  />
                  {totalCount > 0 && (
                    <div className={styles.counter}>{totalCount}</div>
                  )}
                </div>
              </div>
            )}
            {!isExpanded && (
              <div className={styles.wp}>
                {getUserClaim(auth.user, Claims.UserIsSwitchedToAnotherUser) ? (
                  <Avatar className={styles.profil_avatar}>
                    {convertToLetters(
                      getUserClaim(auth.user, Claims.UserSwitchedToUserLabel),
                    )}
                  </Avatar>
                ) : (
                  <Avatar className={styles.profil_avatar}>
                    {convertToLetters(auth.user?.profile.name ?? "")}
                  </Avatar>
                )}
                <ButtonIcon
                  onClick={() => setShowList(!showList)}
                  className={`${styles.btyn} ${showList && styles.showed}`}
                />
                {showList && (
                  <AccountMenu toggle={() => setShowList(false)} />
                )}
              </div>
            )}
          </div>
          {isExpanded && (
            <div className={styles.workspacesWrapper}>
              <div
                onClick={() => setShowList(!showList)}
                className={styles.workName}
              >
                <div className={styles.wname}>{company?.Name}</div>
              </div>
              {showList && (
                <AccountMenu toggle={() => setShowList(false)} />
              )}
            </div>
          )}
        </div>
        {isExpanded && (
          <div className={styles.col2}>
            <div
              onClick={() => handleExpand?.()}
              className={`${styles.expand} ${styles.reo}`}
            >
              <div className={styles.collapseIcon}>
                <MenuIcon />
              </div>
            </div>
          </div>
        )}
      </div>

      {!isMobile && (
        <div ref={addButtonRef} className={styles.addingOption}>
          {isExpanded ? (
            <Button
              className={styles.addBut}
              element={
                <div className="flex items-center gap-2 justify-center">
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: 300,
                      marginTop: "-5px",
                    }}
                  >
                    +
                  </div>{" "}
                  <div>{t("sidebar.createNew")}</div>
                </div>
              }
              onClick={() => setOptions(!options)}
            />
          ) : (
            <AddIcon
              ref={addButtonRef}
              onClick={() => {
                if (isGuest) {
                  setModals({ ...modals, createPost: true, hide: false });
                  return;
                }

                setOptions(!options);
              }}
              className={styles.addIcon}
            />
          )}
          {options && (
            <SidebarAddingOptions
              addButtonRef={addButtonRef}
              toggle={setOptions}
              portal={true}
            />
          )}
        </div>
      )}
      {isMobile && <div className={styles.spacing}></div>}
      <div
        className={classNames(styles.list, {
          [styles.warn]: spaceUsagePercent >= 90,
        })}
      >
        <div ref={testRef2} className={styles.listWrapper}>
          {menus
            .filter((m) => m.visible())
            .map((menu: any) => (
              <>
                {menu.gap && <div className={styles.gap}></div>}
                <SidebarFilter
                  isExpanded={isExpanded ?? true}
                  testRef={testRef}
                  showCounter={menu.showCounter}
                  getFilters={getFilters}
                  id={menu.id}
                  isDesk={menu.isDesk ?? false}
                  key={menu.title}
                  isOpen={menu.isOpen}
                  title={menu.title}
                  name={menu.name ?? "no"}
                  fullId={menu.fullId}
                  isLetter={menu.isLetter ?? false}
                  isAdmin={menu.isAdmin ?? false}
                  filters={menu.filters}
                  filtersHardCoded={menu.filtersHardCoded ?? false}
                  canAddNewFilter={menu.canAddNewFilter}
                  hideExpander={menu.hideExpander ?? false}
                  Icon={menu.icon}
                  handleChangeFilterState={handleChangeFilterState}
                  counters={countersWithDecrees}
                  counterId={menu.counterId}
                  staticId={menu.staticId}
                  isBeta={menu.isBeta}
                />
              </>
            ))}
        </div>
      </div>

      {/* <Can I="accessModule" an="admin">
        <div className={styles.debugTools} ref={degubToolsRef}>
          <LanguageSwitcher></LanguageSwitcher>
          <HollowButton
            element={
              <div className={styles.dbgUiButton}>
                <ViewIcon></ViewIcon>
                {t("sidebar.interface")}{" "}
                <FontAwesomeIcon icon={faAngleDown}></FontAwesomeIcon>
              </div>
            }
            text={t("sidebar.interface")}
            onClick={() => setShowUIDebugModal(!showUIDebugModal)}
          ></HollowButton>
        </div>
      </Can> */}
      <Popper
        anchorEl={degubToolsRef?.current}
        open={showUIDebugModal}
        setOpen={setShowUIDebugModal}
      >
        <div className={styles.dropdown2}>
          <FormControl>
            <FormLabel id="appVariant-label">{t("sidebar.package")}</FormLabel>
            <RadioGroup
              aria-labelledby="appVariant-label"
              defaultValue={uiVariant}
              onChange={(e) => {
                handleDebugChanged(e);
              }}
            >
              <FormControlLabel
                value="free"
                control={<Radio />}
                label={t("sidebar.basic")}
              />
              <FormControlLabel
                value="premium"
                control={<Radio />}
                label={t("sidebar.full")}
              />
            </RadioGroup>
            <FormLabel>{t("sidebar.modules")}</FormLabel>
            <FormGroup>
              <FormControlLabel
                checked={!!uiPrivileges.find((x) => x === EAppModule.Cases)}
                disabled={uiVariant === "premium"}
                control={<Checkbox />}
                onChange={() => {
                  toggleModuleUI(EAppModule.Cases);
                }}
                label={t("sidebar.cases")}
              />
              <FormControlLabel
                checked={
                  !!uiPrivileges.find((x) => x === EAppModule.EncryptedParcels)
                }
                control={<Checkbox />}
                disabled={uiVariant === "premium"}
                onChange={() => {
                  toggleModuleUI(EAppModule.EncryptedParcels);
                }}
                label={t("sidebar.encryptedParcels")}
              />
              <FormControlLabel
                checked={!!uiPrivileges.find((x) => x === EAppModule.Workspace)}
                disabled={uiVariant === "premium"}
                control={<Checkbox />}
                onChange={() => {
                  toggleModuleUI(EAppModule.Workspace);
                }}
                label={t("sidebar.team")}
              />

              <FormControlLabel
                disabled={uiVariant === "premium"}
                checked={!!uiPrivileges.find((x) => x === EAppModule.Tasks)}
                control={<Checkbox />}
                onChange={() => {
                  toggleModuleUI(EAppModule.Tasks);
                }}
                label={t("sidebar.actions")}
              />
              <FormControlLabel
                checked={
                  !!uiPrivileges.find((x) => x === EAppModule.CourtPortal)
                }
                disabled={uiVariant === "premium"}
                control={<Checkbox />}
                onChange={() => {
                  toggleModuleUI(EAppModule.CourtPortal);
                }}
                label={t("sidebar.courtPortal")}
              />
            </FormGroup>
          </FormControl>
        </div>
      </Popper>

      {showQuotaExceeded && (
        <DiskQuotaExceededModal
          onCancel={() => setShowQuotaExceeded(false)}
          onIncreaseSpace={() => {
            setShowQuotaExceeded(false);
            setModals({ ...modals, settings: true, hide: false });
          }}
        />
      )}

      {showLowSpace && (
        <DiskSpaceLowModal
          onRemindLater={() => setShowLowSpace(false)}
          onIncreaseSpace={() => {
            setShowLowSpace(false);
            setModals({ ...modals, settings: true, hide: false });
          }}
        />
      )}

      {!isMobile && !isExpanded && (
        <div
          onClick={() => handleExpand?.()}
          className={`${styles.expand} ${!isExpanded && styles.reo}`}
        >
          <div className={styles.aaaa}>
            <RightIcon />
          </div>
        </div>
      )}

      {isExpanded && (
        <div
          className={styles.diskUsageWrap}
          // Dev mode: kliknięcie w komponent zajętości dysku pokazuje okno
          // ostrzeżenia o kończącym się miejscu (do podglądu projektu).
          onClick={
            import.meta.env.DEV ? () => setShowLowSpace(true) : undefined
          }
          style={import.meta.env.DEV ? { cursor: "pointer" } : undefined}
        >
          <div className={styles.diskUsageHdr}>
            <CloudIcon className={styles.diskIcon} />
            Miejsce na dysku
          </div>
          <div className={styles.diskUsageRow}>
            <div className={styles.diskUsage}>
              {isSpaceUsageReady ? (
                <div
                  className={classNames(styles.diskUsageBar, {
                    [styles.diskUsageDanger]: spaceUsagePercent >= 90,
                  })}
                  style={{ width: `${Math.min(100, spaceUsagePercent)}%` }}
                ></div>
              ) : isFetchingSpaceUsage ? (
                // stan nieokreślony podczas ładowania
                <div
                  className={classNames(
                    styles.diskUsageBar,
                    styles.diskUsageIndeterminate,
                  )}
                ></div>
              ) : (
                // brak danych / poza ładowaniem — 0%
                <div
                  className={styles.diskUsageBar}
                  style={{ width: "0%" }}
                ></div>
              )}
            </div>
          </div>
          <div className={styles.diskUsageInfo}>
            <span>{t("diskTab.yourData")}</span>
            <span>
              {isSpaceUsageReady
                ? `${prettyBytes(spaceUsage!.Used, {
                    binary: true,
                  })} / ${prettyBytes(spaceUsage!.Total, { binary: true })}`
                : "…"}
            </span>
          </div>
          {spaceUsagePercent >= 90 && (
            <div className={styles.diskUsageWarn}>
              Mało miejsca. Zwikększ pojemność
            </div>
          )}
        </div>
      )}

      <div className={styles.logo}>
        <div
          className={`${styles.lite}  ${!isExpanded && styles.reoOpen}`}
        ></div>
        {isExpanded ? (
          <NotaIcon />
        ) : (
          <LogoSmallIcon className={styles.smallLogo} />
        )}

        <Can I="accessModule" an="admin">
          {SignalRContext.connection && (
            <div className={styles.wsStatus}>
              {SignalRContext.connection?.state ===
                HubConnectionState.Connected && (
                <Tooltip title={t("statuses.connected")}>
                  <div
                    className={classNames(styles.state, styles.connected)}
                    onClick={() => {
                      SignalRContext.connection.stop();
                    }}
                  ></div>
                </Tooltip>
              )}
              {SignalRContext.connection?.state ===
                HubConnectionState.Disconnected && (
                <Tooltip title={t("statuses.disconnected")}>
                  <div
                    className={classNames(styles.state, styles.disconnected)}
                    onClick={() => {
                      SignalRContext.connection.start();
                    }}
                  ></div>
                </Tooltip>
              )}

              {(SignalRContext.connection?.state ===
                HubConnectionState.Connecting ||
                SignalRContext.connection?.state ===
                  HubConnectionState.Reconnecting) && (
                <Tooltip title={t("statuses.connecting")}>
                  <div
                    className={classNames(styles.state, styles.connecting)}
                  ></div>
                </Tooltip>
              )}
            </div>
          )}
        </Can>
      </div>
    </div>
  );
};

export default Sidebar;
